import { computed, inject, Injectable, signal } from '@angular/core';
import { debounceTime, filter, Subscription } from 'rxjs';
import { AppStore } from '@app/core/states';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { injectDispatch } from '@ngrx/signals/events';
import { DataSyncEvents } from '@app/domain/events';
import {
  ACTIVE_SQL_MODE,
  CHANGE_TRACKER,
  DATABASE_SERVICE,
  DATABASE_PERSISTENCE_ADAPTER,
} from '../tokens/infrastructure.tokens';

@Injectable({ providedIn: 'root' })
export class DatabaseSyncService {
  private appStore = inject(AppStore);
  private dbSaver = inject(DATABASE_PERSISTENCE_ADAPTER);
  private databaseService = inject(DATABASE_SERVICE);
  private changeTracker = inject(CHANGE_TRACKER);
  private dispatch = injectDispatch(DataSyncEvents);
  private sqliteMode = inject(ACTIVE_SQL_MODE) as 'straight' | 'worker' | 'opfs' | 'native';

  private syncInterval: ReturnType<typeof setInterval> | null = null;
  private hasChangesSubscription: Subscription | null = null;
  private pendingSyncRequested = false;

  private readonly debounceMs = 2000;
  private readonly safetyCheckIntervalMs = 300_000;

  readonly lastSyncTime = signal<number | null>(null);
  readonly isSyncing = signal(false);
  readonly hasPendingChanges = toSignal(this.changeTracker.hasChanges$, {
    initialValue: this.changeTracker.isDirty(),
  });
  readonly isPersistencePending = computed(() => this.hasPendingChanges() || this.isSyncing());
  readonly lastSyncTime$ = toObservable(this.lastSyncTime);

  /**
   * Start periodic database sync when DEK is unlocked.
   * Call this after successful login.
   */
  startPeriodicSync(): void {
    if (this.hasChangesSubscription) {
      return;
    }

    this.hasChangesSubscription = this.changeTracker.hasChanges$
      .pipe(
        filter((hasChanges) => hasChanges),
        debounceTime(this.debounceMs),
      )
      .subscribe(() => {
        this.syncNow();
      });

    this.syncInterval = setInterval(() => {
      const timeSinceLastSync = Date.now() - (this.lastSyncTime() || 0);
      if (this.changeTracker.isDirty() && timeSinceLastSync > 60_000) {
        console.log('⚠️ Safety sync triggered (fallback)');
        this.syncNow();
      }
    }, this.safetyCheckIntervalMs);

    console.log('✓ Database sync started');
  }

  /**
   * Stop periodic sync (on logout).
   */
  stopPeriodicSync(): void {
    if (this.hasChangesSubscription) {
      this.hasChangesSubscription.unsubscribe();
      this.hasChangesSubscription = null;
    }

    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }

    console.log('✓ Database sync stopped');
  }

  /**
   * Manually trigger a sync.
   * Only the leader tab performs actual database writes to IndexedDB.
   */
  async syncNow(): Promise<void> {
    if (!this.appStore.isUnlocked()) {
      console.warn('Cannot sync: DEK not unlocked');
      return;
    }

    if (!this.databaseService.isInitialized()) {
      console.warn('Cannot sync: Database not initialized');
      return;
    }

    // Only leader tab performs database writes
    if (!this.appStore.isLeader()) {
      console.log('⏭️ Skipping sync (not leader tab)');
      this.changeTracker.markClean(); // Clear local dirty flag
      return;
    }

    if (this.isSyncing()) {
      this.pendingSyncRequested = true;
      return;
    }

    try {
      this.isSyncing.set(true);
      this.dispatch.syncStarted();

      if (this.sqliteMode === 'native') {
        this.changeTracker.markClean();
        this.lastSyncTime.set(Date.now());
        console.log('✓ Native SQLite persists changes without IndexedDB snapshot sync');
        this.dispatch.syncCompleted({ timestamp: Date.now() });
        return;
      }

      const dbData = await this.databaseService.exportDatabase();
      await this.dbSaver.save(dbData);
      this.changeTracker.markClean();
      this.lastSyncTime.set(Date.now());
      if (this.sqliteMode === 'opfs') {
        console.log('✓ Database synced to IndexedDB recovery snapshot at', new Date().toISOString());
      } else {
        console.log('✓ Database synced to IndexedDB at', new Date().toISOString());
      }

      // Dispatch event for multi-tab synchronization
      this.dispatch.syncCompleted({ timestamp: Date.now() });
    } catch (error) {
      // Non-fatal: sync runs on a background timer. Throwing here would skip the
      // pendingSyncRequested retry in finally and crash the sync loop.
      // syncFailed event notifies consumers; error is surfaced via GlobalErrorHandler below.
      console.warn('Database sync failed:', error);
      this.dispatch.syncFailed({ error: (error as Error).message });
    } finally {
      this.isSyncing.set(false);

      if (this.pendingSyncRequested) {
        this.pendingSyncRequested = false;
        setTimeout(() => this.syncNow(), 100);
      }
    }
  }
}
